import maplibregl from "maplibre-gl";
import type { Feature, FeatureCollection, LineString, Point } from "geojson";
import type {
  AisPointCollection,
  AisVesselProperties,
} from "./aisData";

const TRAFFIC_SOURCE_ID = "klint-ais-traffic";
const HISTORY_SOURCE_ID = "klint-ais-history";
const TRACK_SOURCE_ID = "klint-ais-track";
const TRACK_LAYER_ID = "klint-ais-track-line";
const HISTORICAL_LAYER_ID = "klint-ais-historical";
const LIVE_HALO_LAYER_ID = "klint-ais-live-halo";
const LIVE_LAYER_ID = "klint-ais-live";
const SELECTED_LAYER_ID = "klint-ais-selected";
const COLLISION_LAYER_ID = "klint-ais-collision-risk";
const VESSEL_IMAGE_ID = "klint-ais-vessel-icon";
const TRACK_GAP_SECONDS = 30 * 60;

type AisDisplay = "live" | "historical";

interface AisDisplayProperties extends AisVesselProperties {
  display: AisDisplay;
}

interface AisTrackProperties {
  sample_date: string;
  day_index: number;
}

interface AisHistorySegmentProperties extends AisVesselProperties {
  display: "historical";
  fade: number;
}

export interface AisVesselSelection extends AisVesselProperties {
  longitude: number;
  latitude: number;
}

interface AisOverlayOptions {
  onSelect: (vessel: AisVesselSelection) => void;
  onClose: () => void;
  onDaysChange: (days: number) => void;
}

function emptyTraffic(): FeatureCollection<Point, AisDisplayProperties> {
  return { type: "FeatureCollection", features: [] };
}

function emptyTrack(): FeatureCollection<LineString, AisTrackProperties> {
  return { type: "FeatureCollection", features: [] };
}

function emptyHistory(): FeatureCollection<LineString, AisHistorySegmentProperties> {
  return { type: "FeatureCollection", features: [] };
}

function displayData(
  collection: AisPointCollection,
  display: AisDisplay,
): FeatureCollection<Point, AisDisplayProperties> {
  return {
    type: "FeatureCollection",
    features: collection.features.map((feature) => ({
      ...feature,
      properties: { ...feature.properties, display },
    })),
  };
}

function localDateKey(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function historicalPathData(
  collection: AisPointCollection,
): FeatureCollection<LineString, AisHistorySegmentProperties> {
  const groups = new Map<string, typeof collection.features>();
  for (const feature of [...collection.features].sort(
    (left, right) => left.properties.timestamp - right.properties.timestamp,
  )) {
    const sampleDate = feature.properties.sample_date ?? localDateKey(feature.properties.timestamp);
    const key = `${sampleDate}:${feature.properties.id}`;
    const group = groups.get(key) ?? [];
    group.push(feature);
    groups.set(key, group);
  }

  const windowSeconds = Math.max(1, collection.window_minutes ?? 60) * 60;
  const features: Array<Feature<LineString, AisHistorySegmentProperties>> = [];
  for (const points of groups.values()) {
    for (let index = 1; index < points.length; index++) {
      const previous = points[index - 1];
      const current = points[index];
      if (current.properties.timestamp - previous.properties.timestamp > TRACK_GAP_SECONDS) {
        continue;
      }
      const meanOffset =
        (Math.abs(previous.properties.offset_seconds ?? 0) +
          Math.abs(current.properties.offset_seconds ?? 0)) /
        2;
      const proximity = Math.max(0, 1 - Math.min(1, meanOffset / windowSeconds));
      features.push({
        type: "Feature",
        properties: {
          ...current.properties,
          display: "historical",
          fade: 0.12 + Math.pow(proximity, 1.35) * 0.58,
        },
        geometry: {
          type: "LineString",
          coordinates: [previous.geometry.coordinates, current.geometry.coordinates],
        },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

function trackData(
  collection: AisPointCollection,
): FeatureCollection<LineString, AisTrackProperties> {
  const groups = new Map<string, typeof collection.features>();
  for (const feature of [...collection.features].sort(
    (left, right) => left.properties.timestamp - right.properties.timestamp,
  )) {
    const key = localDateKey(feature.properties.timestamp);
    const group = groups.get(key) ?? [];
    group.push(feature);
    groups.set(key, group);
  }
  const dates = [...groups.keys()].sort().reverse();
  const features: Array<Feature<LineString, AisTrackProperties>> = [];
  for (let dayIndex = 0; dayIndex < dates.length; dayIndex++) {
    const sampleDate = dates[dayIndex];
    const points = groups.get(sampleDate) ?? [];
    let segment: typeof points = [];
    const flush = () => {
      if (segment.length > 1) {
        features.push({
          type: "Feature",
          properties: { sample_date: sampleDate, day_index: dayIndex },
          geometry: {
            type: "LineString",
            coordinates: segment.map((point) => point.geometry.coordinates),
          },
        });
      }
      segment = [];
    };
    for (const point of points) {
      const previous = segment.at(-1);
      if (
        previous &&
        point.properties.timestamp - previous.properties.timestamp > TRACK_GAP_SECONDS
      ) {
        flush();
      }
      segment.push(point);
    }
    flush();
  }
  return { type: "FeatureCollection", features };
}

function vesselImage(): ImageData {
  const canvas = document.createElement("canvas");
  canvas.width = 48;
  canvas.height = 48;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is unavailable.");
  context.beginPath();
  context.moveTo(24, 3);
  context.lineTo(42, 42);
  context.lineTo(24, 34);
  context.lineTo(6, 42);
  context.closePath();
  context.fillStyle = "#14b8a6";
  context.strokeStyle = "#071722";
  context.lineWidth = 4;
  context.lineJoin = "round";
  context.fill();
  context.stroke();
  return context.getImageData(0, 0, canvas.width, canvas.height);
}

function observedLabel(timestamp: number): string {
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp * 1000));
}

function courseLabel(vessel: AisVesselSelection): string {
  if (vessel.course_source === "unavailable") return "Course unavailable";
  const source = vessel.course_source === "positions"
    ? "estimated from fixes"
    : vessel.course_source === "heading"
      ? "AIS heading"
      : vessel.course_source === "cog"
        ? "AIS COG"
        : "AIS course";
  return `Course ${Math.round(vessel.course_deg)}° true · ${source}`;
}

class AisVesselControl implements maplibregl.IControl {
  readonly element = document.createElement("section");
  private readonly title = document.createElement("strong");
  private readonly observation = document.createElement("span");
  private readonly course = document.createElement("span");
  private readonly speed = document.createElement("span");
  private readonly trackStatus = document.createElement("p");
  private readonly fitButton = document.createElement("button");
  private readonly daysSelect = document.createElement("select");

  constructor(
    private readonly onClose: () => void,
    private readonly onDaysChange: (days: number) => void,
    private readonly onFit: () => void,
  ) {}

  onAdd(): HTMLElement {
    this.element.className = "maplibregl-ctrl ais-vessel-card";
    this.element.hidden = true;
    this.element.setAttribute("aria-label", "Selected AIS vessel");

    const header = document.createElement("header");
    const heading = document.createElement("div");
    const eyebrow = document.createElement("span");
    eyebrow.textContent = "AIS vessel";
    heading.append(eyebrow, this.title);
    const close = document.createElement("button");
    close.type = "button";
    close.className = "ais-vessel-close";
    close.setAttribute("aria-label", "Close vessel details");
    close.textContent = "×";
    close.addEventListener("click", this.onClose);
    header.append(heading, close);

    const details = document.createElement("div");
    details.className = "ais-vessel-meta";
    details.append(this.observation, this.course, this.speed);

    const history = document.createElement("label");
    const historyText = document.createElement("span");
    historyText.textContent = "Route history";
    for (const days of [1, 3, 7]) {
      const option = document.createElement("option");
      option.value = String(days);
      option.textContent = `${days} ${days === 1 ? "day" : "days"}`;
      option.selected = days === 3;
      this.daysSelect.append(option);
    }
    this.daysSelect.addEventListener("change", () => {
      this.onDaysChange(Number(this.daysSelect.value));
    });
    history.append(historyText, this.daysSelect);

    this.trackStatus.className = "ais-track-status";
    this.trackStatus.setAttribute("aria-live", "polite");
    const actions = document.createElement("div");
    actions.className = "ais-vessel-actions";
    this.fitButton.type = "button";
    this.fitButton.textContent = "Fit route";
    this.fitButton.hidden = true;
    this.fitButton.addEventListener("click", this.onFit);
    actions.append(this.fitButton);
    this.element.append(header, details, history, this.trackStatus, actions);
    return this.element;
  }

  onRemove(): void {
    this.element.remove();
  }

  show(vessel: AisVesselSelection, days: number): void {
    this.element.hidden = false;
    this.title.textContent = `MMSI ${vessel.mmsi}`;
    this.observation.textContent = `Observed ${observedLabel(vessel.timestamp)}`;
    this.course.textContent = courseLabel(vessel);
    this.speed.textContent = vessel.speed_knots === null || vessel.speed_knots === undefined
      ? "Speed unavailable"
      : `Speed ${vessel.speed_knots.toFixed(1)} kn${vessel.speed_source === "positions" ? " · estimated from fixes" : " · AIS SOG"}`;
    this.daysSelect.value = String(days);
    this.showTrackStatus(`Loading ${days}-day route…`, false);
  }

  showTrackStatus(message: string, canFit: boolean): void {
    this.trackStatus.textContent = message;
    this.fitButton.hidden = !canFit;
  }

  hide(): void {
    this.element.hidden = true;
    this.fitButton.hidden = true;
  }
}

export class AisOverlay {
  private readonly control: AisVesselControl;
  private traffic = emptyTraffic();
  private history = emptyHistory();
  private track = emptyTrack();

  private readonly onVesselClick = (event: maplibregl.MapLayerMouseEvent): void => {
    const feature = event.features?.[0];
    if (!feature) return;
    const properties = feature.properties as unknown as AisVesselProperties;
    if (!properties?.id || !properties.mmsi) return;
    const coordinates =
      feature.geometry.type === "Point"
        ? feature.geometry.coordinates
        : [event.lngLat.lng, event.lngLat.lat];
    event.preventDefault();
    this.options.onSelect({
      ...properties,
      longitude: Number(coordinates[0]),
      latitude: Number(coordinates[1]),
    });
  };

  private readonly showPointer = (): void => {
    this.map.getCanvas().style.cursor = "pointer";
  };

  private readonly hidePointer = (): void => {
    this.map.getCanvas().style.cursor = "";
  };

  constructor(
    private readonly map: maplibregl.Map,
    private readonly options: AisOverlayOptions,
  ) {
    this.control = new AisVesselControl(
      options.onClose,
      (days) => options.onDaysChange(days),
      () => this.fitTrack(),
    );
    this.map.addControl(this.control, "top-right");
    this.ensureLayers();
    for (const layer of [LIVE_LAYER_ID, HISTORICAL_LAYER_ID]) {
      this.map.on("click", layer, this.onVesselClick);
      this.map.on("mouseenter", layer, this.showPointer);
      this.map.on("mouseleave", layer, this.hidePointer);
    }
  }

  showTraffic(collection: AisPointCollection, display: AisDisplay): number {
    this.ensureLayers();
    this.traffic = display === "live" ? displayData(collection, display) : emptyTraffic();
    this.history = display === "historical" ? historicalPathData(collection) : emptyHistory();
    (this.map.getSource(TRAFFIC_SOURCE_ID) as maplibregl.GeoJSONSource).setData(
      this.traffic,
    );
    (this.map.getSource(HISTORY_SOURCE_ID) as maplibregl.GeoJSONSource).setData(
      this.history,
    );
    return display === "historical" ? this.history.features.length : this.traffic.features.length;
  }

  clearTraffic(): void {
    this.traffic = emptyTraffic();
    this.history = emptyHistory();
    (this.map.getSource(TRAFFIC_SOURCE_ID) as maplibregl.GeoJSONSource | undefined)?.setData(
      this.traffic,
    );
    (this.map.getSource(HISTORY_SOURCE_ID) as maplibregl.GeoJSONSource | undefined)?.setData(
      this.history,
    );
    this.showCollisionRisks([]);
  }

  showCollisionRisks(vesselIds: string[]): void {
    if (!this.map.getLayer(COLLISION_LAYER_ID)) return;
    this.map.setFilter(COLLISION_LAYER_ID, [
      "in",
      ["get", "id"],
      ["literal", vesselIds],
    ]);
  }

  fitTraffic(): boolean {
    const coordinates = [
      ...this.traffic.features.map((feature) => feature.geometry.coordinates),
      ...this.history.features.flatMap((feature) => feature.geometry.coordinates),
    ];
    if (!coordinates.length) return false;
    const bounds = new maplibregl.LngLatBounds();
    for (const coordinate of coordinates) bounds.extend(coordinate as [number, number]);
    if (coordinates.length === 1) {
      this.map.easeTo({ center: coordinates[0] as [number, number], zoom: 12, duration: 650 });
    } else {
      this.map.fitBounds(bounds, {
        padding: { top: 80, right: 70, bottom: 80, left: 70 },
        maxZoom: 12,
        duration: 650,
      });
    }
    return true;
  }

  select(vessel: AisVesselSelection, days: number): void {
    this.map.setFilter(SELECTED_LAYER_ID, ["==", ["get", "id"], vessel.id]);
    this.control.show(vessel, days);
  }

  showTrack(collection: AisPointCollection): void {
    this.track = trackData(collection);
    (this.map.getSource(TRACK_SOURCE_ID) as maplibregl.GeoJSONSource).setData(this.track);
    if (!collection.count) {
      this.control.showTrackStatus("No saved route is available for this vessel.", false);
      return;
    }
    const dayCount = new Set(
      collection.features.map((feature) => localDateKey(feature.properties.timestamp)),
    ).size;
    const truncated = collection.truncated ? " · limited by server" : "";
    this.control.showTrackStatus(
      `${collection.count} fixes across ${dayCount} ${dayCount === 1 ? "day" : "days"}${truncated}`,
      this.track.features.length > 0,
    );
  }

  showTrackError(message: string): void {
    this.track = emptyTrack();
    (this.map.getSource(TRACK_SOURCE_ID) as maplibregl.GeoJSONSource).setData(this.track);
    this.control.showTrackStatus(message, false);
  }

  clearSelection(): void {
    this.track = emptyTrack();
    if (this.map.getLayer(SELECTED_LAYER_ID)) {
      this.map.setFilter(SELECTED_LAYER_ID, ["==", ["get", "id"], ""]);
    }
    (this.map.getSource(TRACK_SOURCE_ID) as maplibregl.GeoJSONSource | undefined)?.setData(
      this.track,
    );
    this.control.hide();
  }

  destroy(): void {
    for (const layer of [LIVE_LAYER_ID, HISTORICAL_LAYER_ID]) {
      this.map.off("click", layer, this.onVesselClick);
      this.map.off("mouseenter", layer, this.showPointer);
      this.map.off("mouseleave", layer, this.hidePointer);
    }
    this.hidePointer();
    this.clearSelection();
    for (const layer of [
      SELECTED_LAYER_ID,
      LIVE_LAYER_ID,
      LIVE_HALO_LAYER_ID,
      COLLISION_LAYER_ID,
      HISTORICAL_LAYER_ID,
      TRACK_LAYER_ID,
    ]) {
      if (this.map.getLayer(layer)) this.map.removeLayer(layer);
    }
    for (const source of [TRAFFIC_SOURCE_ID, HISTORY_SOURCE_ID, TRACK_SOURCE_ID]) {
      if (this.map.getSource(source)) this.map.removeSource(source);
    }
    if (this.map.hasImage(VESSEL_IMAGE_ID)) this.map.removeImage(VESSEL_IMAGE_ID);
    if (this.map.hasControl(this.control)) this.map.removeControl(this.control);
  }

  private fitTrack(): void {
    const coordinates = this.track.features.flatMap((feature) => feature.geometry.coordinates);
    if (!coordinates.length) return;
    const bounds = new maplibregl.LngLatBounds();
    for (const coordinate of coordinates) bounds.extend(coordinate as [number, number]);
    this.map.fitBounds(bounds, {
      padding: { top: 100, right: 70, bottom: 100, left: 70 },
      maxZoom: 14,
      duration: 650,
    });
  }

  private ensureLayers(): void {
    if (!this.map.hasImage(VESSEL_IMAGE_ID)) {
      this.map.addImage(VESSEL_IMAGE_ID, vesselImage(), { pixelRatio: 2 });
    }
    if (!this.map.getSource(TRACK_SOURCE_ID)) {
      this.map.addSource(TRACK_SOURCE_ID, { type: "geojson", data: emptyTrack() });
    }
    if (!this.map.getLayer(TRACK_LAYER_ID)) {
      this.map.addLayer({
        id: TRACK_LAYER_ID,
        type: "line",
        source: TRACK_SOURCE_ID,
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": [
            "match",
            ["get", "day_index"],
            0,
            "#ffb703",
            1,
            "#22d3ee",
            2,
            "#a78bfa",
            3,
            "#fb7185",
            4,
            "#4ade80",
            5,
            "#f97316",
            "#94a3b8",
          ],
          "line-width": ["interpolate", ["linear"], ["zoom"], 5, 2, 12, 4],
          "line-opacity": 0.88,
        },
      });
    }
    if (!this.map.getSource(TRAFFIC_SOURCE_ID)) {
      this.map.addSource(TRAFFIC_SOURCE_ID, { type: "geojson", data: emptyTraffic() });
    }
    if (!this.map.getSource(HISTORY_SOURCE_ID)) {
      this.map.addSource(HISTORY_SOURCE_ID, { type: "geojson", data: emptyHistory() });
    }
    if (!this.map.getLayer(HISTORICAL_LAYER_ID)) {
      this.map.addLayer({
        id: HISTORICAL_LAYER_ID,
        type: "line",
        source: HISTORY_SOURCE_ID,
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#0891b2",
          "line-width": ["interpolate", ["linear"], ["zoom"], 5, 1.5, 12, 3.5],
          "line-opacity": ["get", "fade"],
          "line-blur": 0.35,
        },
      });
    }
    if (!this.map.getLayer(LIVE_HALO_LAYER_ID)) {
      this.map.addLayer({
        id: LIVE_HALO_LAYER_ID,
        type: "circle",
        source: TRAFFIC_SOURCE_ID,
        filter: ["==", ["get", "display"], "live"],
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 5, 12, 9],
          "circle-color": "rgba(7, 23, 34, 0.72)",
          "circle-stroke-color": "#14b8a6",
          "circle-stroke-width": 1,
        },
      });
    }
    if (!this.map.getLayer(LIVE_LAYER_ID)) {
      this.map.addLayer({
        id: LIVE_LAYER_ID,
        type: "symbol",
        source: TRAFFIC_SOURCE_ID,
        filter: ["==", ["get", "display"], "live"],
        layout: {
          "icon-image": VESSEL_IMAGE_ID,
          "icon-size": ["interpolate", ["linear"], ["zoom"], 5, 0.7, 12, 1.05],
          "icon-rotate": ["coalesce", ["get", "course_deg"], 0],
          "icon-rotation-alignment": "map",
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
        },
      });
    }
    if (!this.map.getLayer(COLLISION_LAYER_ID)) {
      this.map.addLayer({
        id: COLLISION_LAYER_ID,
        type: "circle",
        source: TRAFFIC_SOURCE_ID,
        filter: ["in", ["get", "id"], ["literal", []]],
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 10, 12, 16],
          "circle-color": "rgba(229, 72, 77, 0.2)",
          "circle-stroke-color": "#e5484d",
          "circle-stroke-width": 4,
        },
      });
    }
    if (!this.map.getLayer(SELECTED_LAYER_ID)) {
      this.map.addLayer({
        id: SELECTED_LAYER_ID,
        type: "circle",
        source: TRAFFIC_SOURCE_ID,
        filter: ["==", ["get", "id"], ""],
        paint: {
          "circle-radius": ["interpolate", ["linear"], ["zoom"], 5, 8, 12, 13],
          "circle-color": "rgba(0, 0, 0, 0)",
          "circle-stroke-color": "#ffb703",
          "circle-stroke-width": 3,
        },
      });
    }
  }
}
